import { createReducer, on } from '@ngrx/store';
import { IAiConversation, IAiConversationSummary } from '../models/ai-chat.models';
import * as aiChatActions from './ai-chat.actions';

// ── Interfaces ─────────────────────────────────────────────────────────────────

export interface AiChatState {
  drawerOpen: boolean;
  conversationList: IAiConversationSummary[];
  activeConversation: IAiConversation | null;
  activeConversationId: string | null;
  isDraft: boolean;
  loading: {
    conversationList: boolean;
    activeConversation: boolean;
    sendMessage: boolean;
  };
}

// ── Initial State ──────────────────────────────────────────────────────────────

export const initialAiChatState: AiChatState = {
  drawerOpen: false,
  conversationList: [],
  activeConversation: null,
  activeConversationId: null,
  isDraft: false,
  loading: {
    conversationList: false,
    activeConversation: false,
    sendMessage: false,
  },
};

// ── Reducer ────────────────────────────────────────────────────────────────────

export const aiChatReducer = createReducer(
  initialAiChatState,

  // ── Drawer ──
  on(aiChatActions.ToggleAiChatDrawer, (state) => ({
    ...state,
    drawerOpen: !state.drawerOpen,
  })),
  on(aiChatActions.SetAiChatDrawerOpen, (state, { open }) => ({
    ...state,
    drawerOpen: open,
  })),

  // ── Conversation list ──
  on(aiChatActions.LoadConversations, (state) => ({
    ...state,
    loading: { ...state.loading, conversationList: true },
  })),
  on(aiChatActions.LoadConversationsSuccess, (state, { conversations }) => ({
    ...state,
    conversationList: conversations,
    loading: { ...state.loading, conversationList: false },
  })),
  on(aiChatActions.LoadConversationsFailure, (state) => ({
    ...state,
    loading: { ...state.loading, conversationList: false },
  })),

  // ── Active conversation ──
  on(aiChatActions.SelectConversation, (state, { id }) => ({
    ...state,
    activeConversationId: id,
    activeConversation: null,
    isDraft: false,
    loading: { ...state.loading, activeConversation: true },
  })),
  on(aiChatActions.SelectConversationSuccess, (state, { conversation }) => ({
    ...state,
    activeConversation: conversation,
    activeConversationId: conversation.id,
    loading: { ...state.loading, activeConversation: false },
  })),
  on(aiChatActions.SelectConversationFailure, (state) => ({
    ...state,
    loading: { ...state.loading, activeConversation: false },
  })),

  // ── Draft ──
  on(aiChatActions.CreateDraftConversationSuccess, (state, { conversation }) => ({
    ...state,
    activeConversation: conversation,
    activeConversationId: conversation.id,
    isDraft: true,
  })),
  on(aiChatActions.ResolveDraftConversation, (state, { realId }) => ({
    ...state,
    activeConversationId: realId,
    isDraft: false,
  })),

  // ── Delete (pessimistic — remove only on success) ──
  on(aiChatActions.DeleteConversationSuccess, (state, { id }) => {
    const remaining = state.conversationList.filter((c) => c.id !== id);
    const wasActive = state.activeConversationId === id;
    return {
      ...state,
      conversationList: remaining,
      ...(wasActive
        ? {
            activeConversationId: remaining[0]?.id ?? null,
            activeConversation: null,
            isDraft: false,
          }
        : {}),
    };
  }),

  // ── Messaging ──
  on(aiChatActions.SendMessage, (state) => ({
    ...state,
    loading: { ...state.loading, sendMessage: true },
  })),
  on(aiChatActions.SendMessageSuccess, (state, { conversation }) => ({
    ...state,
    activeConversation: conversation,
    loading: { ...state.loading, sendMessage: false },
  })),
  on(aiChatActions.SendMessageError, (state) => ({
    ...state,
    loading: { ...state.loading, sendMessage: false },
  }))
);
